import React, { useMemo, useState } from 'react';
import CourseTheatreRecipe from '../../../flows/CourseTheatreRecipe';
import { generateProgressTree, returnVersionReadyProgress, getProgressAsArr } from '../../../utils/courseFunctions';
import { buildRecipeIndex, isNodeCompleted } from '../../../utils/buildRecipeIndex';
import CourseProgressGrid from '../../../components/CourseProgress/CourseProgressGrid';
import { useMutation } from 'react-apollo';
import gql from 'graphql-tag';

// Built once at module level — safe since CourseTheatreRecipe is a static import.
const recipeIdx = buildRecipeIndex(CourseTheatreRecipe);

function buildDisplayTreeFromProgressById(progressById) {
  return {
    courses: recipeIdx.courseIds.map(courseId => ({
      id: courseId,
      completed: isNodeCompleted(recipeIdx, courseId, progressById),
      lessons: (recipeIdx.childrenOf[courseId] || []).map(lessonId => ({
        id: lessonId,
        completed: isNodeCompleted(recipeIdx, lessonId, progressById),
        steps: (recipeIdx.childrenOf[lessonId] || []).map(stepId => ({
          id: stepId,
          completed: isNodeCompleted(recipeIdx, stepId, progressById),
          stepItems: (recipeIdx.childrenOf[stepId] || []).map(stepItemId => ({
            id: stepItemId,
            completed: !!progressById[stepItemId]?.completed,
          })),
        })),
      })),
    })),
  };
}

export default function CourseProgressOverview({ userProgress, userId }) {
  // Derive initial, version-ready progress for local display state
  const initialDisplayProgress = useMemo(() => {
    if (!userProgress || Object.keys(userProgress).length === 0) return null;
    // New flat format — build display tree from progressById
    if (userProgress.progressById) {
      return buildDisplayTreeFromProgressById(userProgress.progressById);
    }
    // Old nested tree format
    return returnVersionReadyProgress(generateProgressTree(CourseTheatreRecipe), userProgress);
  }, [userProgress]);
  const [displayProgress, setDisplayProgress] = useState(initialDisplayProgress);
  const [, lessonIdx, stepIdx, stepItemIdx] = getProgressAsArr(displayProgress || { courses: [] });

  const SET_USER_PROGRESS_BY_ID = gql`
    mutation SetUserProgressById($id: String!, $userProgress: JSON!) {
      setUserProgressById(id: $id, userProgress: $userProgress) {
        id
        userProgress
        __typename
      }
    }
  `;
  const [mutateUpdateUser] = useMutation(SET_USER_PROGRESS_BY_ID);

  const handleUpdate = async (nextProgress) => {
    try {
      if (!userId) return;
      await mutateUpdateUser({
        variables: {
          id: userId,
          userProgress: nextProgress
        }
      });
      setDisplayProgress(nextProgress);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to update user progress', e);
    }
  };
  if (!displayProgress || !displayProgress.courses || !displayProgress.courses.length) {
    return <div className="no-data">No data</div>;
  }
  return (
    <div style={{ padding: '0 18px 8px 18px' }}>
      <CourseProgressGrid 
        courses={displayProgress.courses}
        currentLessonIndex={lessonIdx}
        currentStepIndex={stepIdx}
        currentStepItemIndex={stepItemIdx}
        courseRecipe={CourseTheatreRecipe}
        onUpdateProgress={handleUpdate}
      />
    </div>
  );
}


